import { FarmDetailsState } from "../../../reducers/farmDetailsReducer";
import {
  IntegerMonth,
  PlantationProjectType,
  mapPlantationMethodToPlantationProjectType
} from "../../types/sum";
import { Polygon } from "../types";

export type BadRequestResponse = {
  detail?: string;
  status?: number;
  title?: string;
  type?: string;
};

export type CommonParameters = {
  projectArea: Polygon;
  startYear: number;
  startMonth: IntegerMonth;
};

export type PlantationAvailableSpeciesTypes = Array<PlantationAvailableSpecies>;

export type PlantationAvailableSpecies = {
  method: PlantationSchedule;
  availableSpecies: Array<PlantationSpecies | PlantationSpeciesWithYearOptions>;
};

export type PlantationSpeciesWithYearOptions = {
  species: PlantationSpecies;
  availableThinningAndClearfellYears: Array<PlantationThinningAndClearfellYearsType>;
};

export type PlantationSpeciesTransitionWithYearOptions = {
  species: PlantationSpeciesTransition;
  availableThinningAndClearfellYears: Array<PlantationThinningAndClearfellYearsType>;
};

export enum PlantationSpecies {
  Pinus_Radiata = "PinusRadiata",
  Eucalyptus_Regnans = "EucalyptusRegnans",
  Eucalyptus_Cladocalyx = "EucalyptusCladocalyx",
  Eucalyptus_Globulus = "EucalyptusGlobulus",
  Eucalyptus_Nitens = "EucalyptusNitens",
  Pinus_Radiata_Short_Rotation = "PinusRadiata_ShortRotation",
  Eucalyptus_Regnans_Short_Rotation = "EucalyptusRegnans_ShortRotation",
  Eucalyptus_Globulus_Short_Rotation = "EucalyptusGlobulus_ShortRotation",
  Eucalyptus_Nitens_Short_Rotation = "EucalyptusNitens_ShortRotation",
  Eucalyptus_Grandis = "EucalyptusGrandis",
  Other_Eucalypts = "OtherEucalypts",
  Other_Softwoods = "OtherSoftwoods",
  Pinus_Hybrids = "PinusHybrids"
}

export enum PlantationSpeciesTransition {
  OtherEucalypts_to_PinusHybrids = "OtherEucalypts_to_PinusHybrids",
  OtherEucalypts_to_OtherEucalypts = "OtherEucalypts_to_OtherEucalypts",
  PinusHybrids_to_OtherEucalypts = "PinusHybrids_to_OtherEucalypts",
  PinusRadiata_to_PinusRadiata = "PinusRadiata_to_PinusRadiata"
}

export enum PlantationSchedule {
  // vic only
  SCHEDULE_1_LONG_ROTATION = "S1L",
  SCHEDULE_1_SHORT_ROTATION = "S1S",
  SCHEDULE_2_TO = "S2_TO",
  SCHEDULE_2_FROM = "S2_FROM",
  SCHEDULE_3_LONG_ROTATION = "S3L",
  SCHEDULE_3_SHORT_ROTATION = "S3S",
  FARM_FORESTRY = "FF",
  // nsw only
  SCHEDULE_1 = "S1",
  SCHEDULE_2 = "S2",
  SCHEDULE_3 = "S3",
  // common
  SCHEDULE_4_REMNANT_PLANTATION_A = "S4RPA",
  SCHEDULE_4_PERMANENT_PLANTATION_EX_COMMERCIAL = "S4PPXC"
}

export function getDisplayTextForPlantationSpecies(
  species: PlantationSpecies
): string | undefined {
  switch (species) {
    case PlantationSpecies.Pinus_Radiata:
    case PlantationSpecies.Pinus_Radiata_Short_Rotation:
      return "Pinus radiata (Monterey pine)";
    case PlantationSpecies.Eucalyptus_Cladocalyx:
      return "Eucalyptus cladocalyx (Sugar gum)";
    case PlantationSpecies.Eucalyptus_Regnans:
    case PlantationSpecies.Eucalyptus_Regnans_Short_Rotation:
      return "Eucalyptus regnans (Stringy gum / Mountain ash)";
    case PlantationSpecies.Eucalyptus_Globulus:
    case PlantationSpecies.Eucalyptus_Globulus_Short_Rotation:
      return "Eucalyptus globulus (Blue gum)";
    case PlantationSpecies.Eucalyptus_Nitens:
    case PlantationSpecies.Eucalyptus_Nitens_Short_Rotation:
      return "Eucalyptus nitens (Shining gum)";
    case PlantationSpecies.Eucalyptus_Grandis:
      return "Eucalyptus grandis (Flooded gum / Rose gum)";
    case PlantationSpecies.Other_Eucalypts:
      return "Other Eucalypt species";
    case PlantationSpecies.Other_Softwoods:
      return "Other Softwood species";
    case PlantationSpecies.Pinus_Hybrids:
      return "Pinus hybrids";

    default:
      return undefined;
  }
}

export enum PlantationThinningAndClearfellYearsType {
  // static options for vic
  Years_13_18_and_24_to_35 = "Y_13_18_24_to_35",
  Years_13_and_18_to_30 = "Y_13_18_to_30",
  // additional options for nsw
  Years_14_and_21_to_30 = "Y_14_21_to_30",
  Years_15_and_25_to_35 = "Y_15_25_to_35",
  Years_18_and_28_to_40 = "Y_18_28_to_40",
  Years_15_to_30 = "Y_15_to_30",
  Years_16_to_30 = "Y_16_to_30",
  Years_20_to_35 = "Y_20_to_35",
  Years_20_to_40 = "Y_20_to_40",
  Years_20_to_45 = "Y_20_to_45",
  Years_to_15 = "Y_to_15",
  Years_to_25 = "Y_to_25",
  Years_to_30 = "Y_to_30",
  Years_to_35 = "Y_to_35",
  // Baseline rotation
  BR_Years_13_18_and_24_to_35 = "BR_Y_13_18_24_to_35",
  BR_Years_13_and_18_to_30 = "BR_Y_13_18_to_30",
  BR_Years_14_and_21_to_30 = "BR_Y_14_21_to_30",
  BR_Years_15_and_25_to_35 = "BR_Y_15_25_to_35",
  BR_Years_18_and_28_to_40 = "BR_Y_18_28_to_40",
  BR_Years_15_to_30 = "BR_Y_15_to_30",
  BR_Years_16_to_30 = "BR_Y_16_to_30",
  BR_Years_20_to_35 = "BR_Y_20_to_35",
  BR_Years_20_to_40 = "BR_Y_20_to_40",
  BR_Years_20_to_45 = "BR_Y_20_to_45",
  BR_Years_to_15 = "BR_Y_to_15",
  BR_Years_to_25 = "BR_Y_to_25",
  BR_Years_to_30 = "BR_Y_to_30",
  BR_Years_to_35 = "BR_Y_to_35"
}

export function getDisplayTextForPlantationThinningAndClearfellYearsType(
  yearOptions: PlantationThinningAndClearfellYearsType
): string | undefined {
  switch (yearOptions) {
    // static options for vic
    case PlantationThinningAndClearfellYearsType.Years_13_and_18_to_30:
      return "Thinning: 13 and 18 years; Clearfell: 30 years";
    case PlantationThinningAndClearfellYearsType.Years_13_18_and_24_to_35:
      return "Thinning: 13, 18 and 24 years; Clearfell: 35 years";
    // additional options for nsw
    case PlantationThinningAndClearfellYearsType.Years_14_and_21_to_30:
      return "Thinning: 14 and 21 years; Clearfell: 30 years";
    case PlantationThinningAndClearfellYearsType.Years_15_and_25_to_35:
      return "Thinning: 15 and 25 years; Clearfell: 35 years";
    case PlantationThinningAndClearfellYearsType.Years_18_and_28_to_40:
      return "Thinning: 18 and 28 years; Clearfell: 40 years";
    case PlantationThinningAndClearfellYearsType.Years_15_to_30:
      return "Thinning: 15 years; Clearfell: 30 years";
    case PlantationThinningAndClearfellYearsType.Years_16_to_30:
      return "Thinning: 16 years; Clearfell: 30 years";
    case PlantationThinningAndClearfellYearsType.Years_20_to_35:
      return "Thinning: 20 years; Clearfell: 35 years";
    case PlantationThinningAndClearfellYearsType.Years_20_to_40:
      return "Thinning: 20 years; Clearfell: 40 years";
    case PlantationThinningAndClearfellYearsType.Years_20_to_45:
      return "Thinning: 20 years; Clearfell: 45 years";
    case PlantationThinningAndClearfellYearsType.Years_to_15:
      return "Clearfell: 15 years";
    case PlantationThinningAndClearfellYearsType.Years_to_25:
      return "Clearfell: 25 years";
    case PlantationThinningAndClearfellYearsType.Years_to_30:
      return "Clearfell: 30 years";
    case PlantationThinningAndClearfellYearsType.Years_to_35:
      return "Clearfell: 35 years";
    // Baseline rotation
    case PlantationThinningAndClearfellYearsType.BR_Years_13_and_18_to_30:
      return "Baseline rotation: 30 years; Thinning: 13 and 18 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_13_18_and_24_to_35:
      return "Baseline rotation: 35 years; Thinning: 13, 18 and 24 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_14_and_21_to_30:
      return "Baseline rotation: 30 years; Thinning: 14 and 21 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_15_and_25_to_35:
      return "Baseline rotation: 35 years; Thinning: 15 and 25 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_18_and_28_to_40:
      return "Baseline rotation: 40 years; Thinning: 18 and 28 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_15_to_30:
      return "Baseline rotation: 30 years; Thinning: 15 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_16_to_30:
      return "Baseline rotation: 30 years; Thinning: 16 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_20_to_35:
      return "Baseline rotation: 35 years; Thinning: 20 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_20_to_40:
      return "Baseline rotation: 40 years; Thinning: 20 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_20_to_45:
      return "Baseline rotation: 45 years; Thinning: 20 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_to_15:
      return "Baseline rotation: 15 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_to_25:
      return "Baseline rotation: 25 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_to_30:
      return "Baseline rotation: 30 years";
    case PlantationThinningAndClearfellYearsType.BR_Years_to_35:
      return "Baseline rotation: 35 years";
    default:
      return undefined;
  }
}

export type LocationInVictoria = {
  completelyInsideVic: boolean;
  percentageInsideVic: number;
};

export type LocationInNewSouthWales = {
  completelyInsideRegion: boolean;
  percentageInsideRegion: number;
};

export type LocationInACT = {
  completelyInsideRegion: boolean;
  percentageInsideRegion: number;
};

const BASELINE_ROTATION_YEAR_PREFIX = "BR_";

export function convertToRotationLengthYearsType(value: string) {
  return (BASELINE_ROTATION_YEAR_PREFIX +
    value) as PlantationThinningAndClearfellYearsType;
}

export function mapValueToPlantationThinningAndClearfellTypeKey(value: string) {
  switch (value) {
    case "Y_13_18_24_to_35":
      return PlantationThinningAndClearfellYearsType.Years_13_18_and_24_to_35;
    case "Y_13_18_to_30":
      return PlantationThinningAndClearfellYearsType.Years_13_and_18_to_30;
    case "Y_14_21_to_30":
      return PlantationThinningAndClearfellYearsType.Years_14_and_21_to_30;
    case "Y_15_25_to_35":
      return PlantationThinningAndClearfellYearsType.Years_15_and_25_to_35;
    case "Y_18_28_to_40":
      return PlantationThinningAndClearfellYearsType.Years_18_and_28_to_40;
    case "Y_15_to_30":
      return PlantationThinningAndClearfellYearsType.Years_15_to_30;
    case "Y_16_to_30":
      return PlantationThinningAndClearfellYearsType.Years_16_to_30;
    case "Y_20_to_35":
      return PlantationThinningAndClearfellYearsType.Years_20_to_35;
    case "Y_20_to_40":
      return PlantationThinningAndClearfellYearsType.Years_20_to_40;
    case "Y_20_to_45":
      return PlantationThinningAndClearfellYearsType.Years_20_to_45;
    case "Y_to_15":
      return PlantationThinningAndClearfellYearsType.Years_to_15;
    case "Y_to_25":
      return PlantationThinningAndClearfellYearsType.Years_to_25;
    case "Y_to_30":
      return PlantationThinningAndClearfellYearsType.Years_to_30;
    case "Y_to_35":
      return PlantationThinningAndClearfellYearsType.Years_to_35;
    // Baseline rotation
    // note: for baseline rotation, the api still uses "Y_to" instead of "BR_Y_to"
    // case "BR_Y_to_15":
    //   return PlantationThinningAndClearfellYearsType.BR_Years_to_15;
    // case "BR_Y_to_25":
    //   return PlantationThinningAndClearfellYearsType.BR_Years_to_25;
    // case "BR_Y_to_30":
    //   return PlantationThinningAndClearfellYearsType.BR_Years_to_30;
    // case "BR_Y_to_35":
    //   return PlantationThinningAndClearfellYearsType.BR_Years_to_35;

    default:
      return undefined;
  }
}

export const VIC_DEFAULT_YEAR_OPTIONS = [
  PlantationThinningAndClearfellYearsType.Years_13_and_18_to_30,
  PlantationThinningAndClearfellYearsType.Years_13_18_and_24_to_35
];

export function setAvailableSpeciesDefaultYearOptions(
  availableSpecies?: Array<PlantationSpecies>,
  defaultYearOptions?: Array<PlantationThinningAndClearfellYearsType>
): Array<PlantationSpeciesWithYearOptions> | undefined {
  if (!availableSpecies) return undefined;

  return availableSpecies.map(species => ({
    species,
    availableThinningAndClearfellYears: defaultYearOptions ?? []
  }));
}

export enum PlantationReplantOptionType {
  Same_As_Current = "SameAsCurrent",
  Environmental_Planting = "EnvironmentalPlanting"
}

export function mapValueToPlantationReplantOptionType(value: string) {
  switch (value) {
    case PlantationReplantOptionType.Same_As_Current:
      return PlantationReplantOptionType.Same_As_Current;
    case PlantationReplantOptionType.Environmental_Planting:
      return PlantationReplantOptionType.Environmental_Planting;
  }
}

export type PlantationRequest = {
  projectArea: Polygon;
  species?: PlantationSpecies | undefined;
  currentSpecies?: PlantationSpecies | undefined;
  midRotationFertilizer?: boolean | undefined;
  thinningAndClearfellYears?:
    | PlantationThinningAndClearfellYearsType
    | undefined;
  replantOption?: PlantationReplantOptionType | undefined;
  fromSpecies?: PlantationSpecies | undefined;
  toSpecies?: PlantationSpecies | undefined;
  speciesTransition?: PlantationSpeciesTransition | undefined;
  rotationLength?: PlantationThinningAndClearfellYearsType | undefined;
};

export type PlantationProjectResponse = {
  projectAccusTotal: number;
  projectAccusPerYear: number;
  projectYears: number;
  projectArea: number;
  warnings: Array<string>;
};

export type AllPlantationProjectResponse = {
  projects: Array<PlantationProjectResponse>;
};

export function mapSpeciesNameToPlantationSpecies(
  speciesName: string
): PlantationSpecies | undefined {
  switch (speciesName) {
    case "PinusRadiata":
      return PlantationSpecies.Pinus_Radiata;
    case "EucalyptusRegnans":
      return PlantationSpecies.Eucalyptus_Regnans;
    case "EucalyptusCladocalyx":
      return PlantationSpecies.Eucalyptus_Cladocalyx;
    case "EucalyptusGlobulus":
      return PlantationSpecies.Eucalyptus_Globulus;
    case "EucalyptusNitens":
      return PlantationSpecies.Eucalyptus_Nitens;
    case "PinusRadiata_ShortRotation":
      return PlantationSpecies.Pinus_Radiata_Short_Rotation;
    case "EucalyptusRegnans_ShortRotation":
      return PlantationSpecies.Eucalyptus_Regnans_Short_Rotation;
    case "EucalyptusGlobulus_ShortRotation":
      return PlantationSpecies.Eucalyptus_Globulus_Short_Rotation;
    case "EucalyptusNitens_ShortRotation":
      return PlantationSpecies.Eucalyptus_Nitens_Short_Rotation;
    case "EucalyptusGrandis":
      return PlantationSpecies.Eucalyptus_Grandis;
    case "OtherEucalypts":
      return PlantationSpecies.Other_Eucalypts;
    case "OtherSoftwoods":
      return PlantationSpecies.Other_Softwoods;
    case "PinusHybrids":
      return PlantationSpecies.Pinus_Hybrids;
    default:
      return undefined;
  }
}

export function mapValueToPlantationSpeciesTransitionType(
  value: string
): PlantationSpeciesTransition | undefined {
  switch (value) {
    case "OtherEucalypts_to_PinusHybrids":
      return PlantationSpeciesTransition.OtherEucalypts_to_PinusHybrids;
    case "OtherEucalypts_to_OtherEucalypts":
      return PlantationSpeciesTransition.OtherEucalypts_to_OtherEucalypts;
    case "PinusHybrids_to_OtherEucalypts":
      return PlantationSpeciesTransition.PinusHybrids_to_OtherEucalypts;
    case "PinusRadiata_to_PinusRadiata":
      return PlantationSpeciesTransition.PinusRadiata_to_PinusRadiata;
    default:
      return undefined;
  }
}

export function mapMethodToPlantationProjectTypes(
  method: string
): PlantationProjectType[] {
  let availables: PlantationProjectType[] = [];
  switch (method) {
    case PlantationSchedule.SCHEDULE_1_LONG_ROTATION:
    case PlantationSchedule.SCHEDULE_1_SHORT_ROTATION:
    case PlantationSchedule.SCHEDULE_1:
      availables.push(PlantationProjectType.EstablishingNewPlantationForest);
      break;
    case PlantationSchedule.SCHEDULE_2_FROM:
    case PlantationSchedule.SCHEDULE_2_TO:
    case PlantationSchedule.SCHEDULE_2:
      availables.push(
        PlantationProjectType.PlantationForestActivites,
        PlantationProjectType.ShortToLongRotation
      );
      break;
    case PlantationSchedule.SCHEDULE_3_LONG_ROTATION:
    case PlantationSchedule.SCHEDULE_3_SHORT_ROTATION:
    case PlantationSchedule.SCHEDULE_3:
      availables.push(
        PlantationProjectType.PlantationForestActivites,
        PlantationProjectType.ContinuingRotationalHarvest
      );
      break;
    case PlantationSchedule.SCHEDULE_4_PERMANENT_PLANTATION_EX_COMMERCIAL:
    case PlantationSchedule.SCHEDULE_4_REMNANT_PLANTATION_A:
      availables.push(
        PlantationProjectType.PlantationForestActivites,
        PlantationProjectType.TransitioningToPermanentForest
      );
      break;
    case PlantationSchedule.FARM_FORESTRY:
      availables.push(PlantationProjectType.FarmingForestry);
      break;
  }
  return availables;
}

export function getAvailablePlantationTypesFromResponse(
  t: PlantationAvailableSpeciesTypes
): PlantationProjectType[] {
  let results: PlantationProjectType[] = [];
  t.forEach(i => {
    if (i.availableSpecies.length > 0) {
      let result = mapPlantationMethodToPlantationProjectType(i.method);
      if (result !== undefined && result !== null && result?.length > 0) {
        results.concat(result);
      }
    }
  });
  return results.filter((v, i, a) => a.indexOf(v) === i);
}

// Schedule 2 helper functions

// for "S2_FROM", "S2_TO", and static thinning year options
interface SetOptionsParams {
  from?: PlantationSpecies[] | null;
  to?: PlantationSpecies[] | null;
  defaultYearOptions?: PlantationThinningAndClearfellYearsType[];
}

export function setFromToAndYearOptions({
  from,
  to,
  defaultYearOptions
}: SetOptionsParams): Partial<
  Record<PlantationSpecies, Array<PlantationSpeciesWithYearOptions>>
> {
  if (
    !from ||
    !Array.isArray(from) ||
    !to ||
    !Array.isArray(to) ||
    !defaultYearOptions
  ) {
    return {} as Partial<
      Record<PlantationSpecies, Array<PlantationSpeciesWithYearOptions>>
    >;
  }
  let res: Partial<
    Record<PlantationSpecies, Array<PlantationSpeciesWithYearOptions>>
  > = {};

  from.forEach(f => {
    const toAndYearOptions = setAvailableSpeciesDefaultYearOptions(
      to,
      defaultYearOptions
    );

    if (!!toAndYearOptions) {
      res[f] = toAndYearOptions;
    }
  });
  return res;
}

// for "S2", dynamically set all to and year options for from

export function setToAndYearOptionsForFrom(
  transitionsWithYears?: Array<PlantationSpeciesTransitionWithYearOptions> | null
): Partial<Record<PlantationSpecies, Array<PlantationSpeciesWithYearOptions>>> {
  if (!transitionsWithYears || !Array.isArray(transitionsWithYears)) {
    return {} as Partial<
      Record<PlantationSpecies, Array<PlantationSpeciesWithYearOptions>>
    >;
  }

  // group transitions by "from" species
  const grouped: Partial<
    Record<PlantationSpecies, PlantationSpeciesWithYearOptions[]>
  > = {};
  transitionsWithYears.forEach(
    ({ species, availableThinningAndClearfellYears }) => {
      const [fromStr, toStr] = species.split("_to_");
      if (!fromStr || !toStr) return;

      const from = fromStr as PlantationSpecies;
      const to = toStr as PlantationSpecies;

      if (!grouped[from]) {
        grouped[from] = [];
      }
      grouped[from]!.push({
        species: to,
        availableThinningAndClearfellYears
      });
    }
  );
  return grouped;
}

export function formatPlantationspeciesTransition({
  fromSpecies,
  toSpecies
}: {
  fromSpecies?: PlantationSpecies;
  toSpecies?: PlantationSpecies;
}): PlantationSpeciesTransition | undefined {
  if (!fromSpecies || !toSpecies) {
    return undefined;
  }
  return `${fromSpecies}_to_${toSpecies}` as PlantationSpeciesTransition;
}

export function checkCompletelyInsideVic(farmDetails: FarmDetailsState) {
  return farmDetails.inVic?.completelyInsideVic === true;
}
export function checkCompletelyInsideNsw(farmDetails: FarmDetailsState) {
  // in-nsw-qld
  return farmDetails.inNsw?.completelyInsideRegion === true;
}
export function checkCompletelyInsideAct(farmDetails: FarmDetailsState) {
  return farmDetails.inAct?.completelyInsideRegion === true;
}
